import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, CreditCard, ClipboardCheck, Building2, ChevronRight, Shield } from "lucide-react";
import { superApi as platformApi } from "@/lib/superApi";

const SUPER_BRANCH_KEY = "super_branch_id";

function superApi() {
  const token = localStorage.getItem("super_token");
  return {
    get: (url) => api.get(url, { headers: { Authorization: `Bearer ${token}` } }),
    put: (url, body) => api.put(url, body, { headers: { Authorization: `Bearer ${token}` } }),
  };
}

export default function SuperSettings() {
  const [platform, setPlatform] = useState(null);
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(localStorage.getItem(SUPER_BRANCH_KEY) || "");
  const [paymentsLicensed, setPaymentsLicensed] = useState(false);
  const [attendanceLicensed, setAttendanceLicensed] = useState(true);
  const [savingPayments, setSavingPayments] = useState(false);
  const [savingAttendance, setSavingAttendance] = useState(false);

  const loadBranches = () =>
    superApi()
      .get("/super/branches")
      .then((r) => {
        const list = r.data || [];
        setBranches(list);
        const stored = localStorage.getItem(SUPER_BRANCH_KEY);
        const tepe = list.find((b) => b.slug === "tepeapulco");
        const active = stored && list.some((b) => b.id === stored) ? stored : tepe?.id || list[0]?.id || "";
        if (active) {
          setBranchId(active);
          localStorage.setItem(SUPER_BRANCH_KEY, active);
        }
      })
      .catch(() => toast.error("No se pudieron cargar las sucursales"));

  const loadSettings = () => {
    superApi()
      .get("/super/settings")
      .then((r) => {
        setData(r.data);
        setPaymentsLicensed(!!r.data?.online_payments_licensed);
        setAttendanceLicensed(r.data?.attendance_licensed !== false);
      })
      .catch(() => toast.error("No se pudo cargar configuración"));
  };

  useEffect(() => {
    loadBranches();
    platformApi.get("/super/platform").then((r) => setPlatform(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!branchId) return;
    localStorage.setItem(SUPER_BRANCH_KEY, branchId);
    loadSettings();
  }, [branchId]);

  const onBranchChange = (id) => {
    setBranchId(id);
    setData(null);
  };

  const savePayments = async () => {
    setSavingPayments(true);
    try {
      const { data: updated } = await superApi().put("/super/settings", {
        online_payments_licensed: paymentsLicensed,
      });
      setData(updated);
      setPaymentsLicensed(!!updated?.online_payments_licensed);
      toast.success("Licencia de pagos actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingPayments(false);
    }
  };

  const saveAttendance = async () => {
    setSavingAttendance(true);
    try {
      const { data: updated } = await superApi().put("/super/settings/attendance-license", {
        attendance_licensed: attendanceLicensed,
      });
      setData(updated);
      setAttendanceLicensed(updated?.attendance_licensed !== false);
      toast.success("Licencia de asistencia actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingAttendance(false);
    }
  };

  const activeBranch = branches.find((b) => b.id === branchId);

  if (!data) return <div className="text-white/40">Cargando…</div>;

  const quotaLabel = platform
    ? platform.max_branches == null
      ? `${platform.branch_count} sucursales — ${platform.plan_name}`
      : `${platform.branch_count} de ${platform.max_branches} sucursales — ${platform.plan_name}`
    : "";

  return (
    <div className="space-y-10">
      {platform && (
        <Link
          to="/super/cliente"
          className="glass p-5 rounded-sm flex items-center justify-between gap-4 hover:border-[#FFD700]/30 border border-white/10 transition-colors group"
        >
          <div className="flex items-start gap-3">
            <Shield className="text-[#FFD700] shrink-0 mt-0.5" size={20} />
            <div>
              <div className="text-xs uppercase tracking-widest text-[#E63946]">Plan comercial del cliente</div>
              <div className="font-display text-2xl text-white mt-1">{platform.plan_name}</div>
              <div className="text-sm text-white/60">{platform.plan_tagline} · {quotaLabel}</div>
              <div className="text-xs text-white/40 mt-1">
                Admins locales: {platform.branch_admins_allowed ? "Sí" : "No"} ·
                Panel admin: {platform.admin_licensed ? "Licenciado" : "Sin licencia"}
              </div>
            </div>
          </div>
          <span className="text-sm text-[#FFD700] flex items-center gap-1 shrink-0 group-hover:gap-2 transition-all">
            Cambiar plan <ChevronRight size={16} />
          </span>
        </Link>
      )}

      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700]">Plataforma</div>
        <h1 className="font-display text-4xl text-white mt-1">Módulos del gimnasio</h1>
        <p className="text-white/60 mt-2 max-w-xl">
          Ajuste fino de pagos y asistencia <strong className="text-white/60">por sucursal</strong>.
          Los valores por defecto vienen del plan comercial.
        </p>
      </div>

      <div className="glass p-6 rounded-sm space-y-3 max-w-md">
        <label className="block space-y-1">
          <span className="text-xs text-white/60 flex items-center gap-2">
            <Building2 size={14} /> Sucursal
          </span>
          <select className="input-dark w-full" value={branchId} onChange={(e) => onBranchChange(e.target.value)}>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <p className="text-sm text-white/60">
          Gimnasio: <strong className="text-white">{data.gym_name || activeBranch?.name}</strong>
        </p>
      </div>

      <div className="glass p-6 rounded-sm space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={paymentsLicensed}
            onChange={(e) => setPaymentsLicensed(e.target.checked)}
          />
          <div>
            <div className="font-medium text-white flex items-center gap-2">
              <CreditCard size={18} className="text-[#FFD700]" />
              Licenciar pagos en línea (Mercado Pago)
            </div>
            <p className="text-xs text-white/60 mt-1">
              Si está desactivado, solo efectivo y transferencia (con confirmación manual).
            </p>
          </div>
        </label>

        <div className="text-sm text-white/60 border-t border-white/10 pt-4">
          <div>MP habilitado en gym: {data.mp_enabled ? "Sí" : "No"}</div>
          <div>Checkout disponible: {data.online_payments_available ? "Sí" : "No"}</div>
        </div>

        <button onClick={savePayments} disabled={savingPayments} className="btn-primary text-sm">
          <Save size={14} /> Guardar licencia de pagos
        </button>
      </div>

      <div className="glass p-6 rounded-sm space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={attendanceLicensed}
            onChange={(e) => setAttendanceLicensed(e.target.checked)}
          />
          <div>
            <div className="font-medium text-white flex items-center gap-2">
              <ClipboardCheck size={18} className="text-green-500" />
              Licenciar módulo de asistencia (kiosk)
            </div>
            <p className="text-xs text-white/60 mt-1">
              Si está desactivado, el admin no puede abrir el kiosk ni activar asistencia en configuración.
              El historial en admin sigue visible.
            </p>
          </div>
        </label>

        <div className="text-sm text-white/60 border-t border-white/10 pt-4">
          <div>Asistencia activa en gym: {data.attendance_enabled ? "Sí" : "No"}</div>
          <div>Kiosk disponible: {data.attendance_available ? "Sí" : "No"}</div>
        </div>

        <button
          onClick={saveAttendance}
          disabled={savingAttendance}
          className="btn-primary text-sm bg-green-700 hover:bg-green-600"
        >
          <Save size={14} /> Guardar licencia de asistencia
        </button>
      </div>
    </div>
  );
}
